import classNames from 'classnames';
import { AccordionStyleFour } from 'common/accordion/accordion-style-4.js';
import { Row, SmallTable } from 'components/tables/accordion-table.js';
import { ColumnLayoutContext } from 'page-context/column-layout/column-layout-context.js';
import { useContext } from 'react';
import { TidePeak } from 'types/tides.js';
import { createArrayFromNumber } from 'utilities/array.js';
import { ITideArr, ITidesTimetable } from './main.js';
import { TidesRows } from './tide-rows.js';
import { EmptyTideCell, createTideHeaderDate } from './utils.js';

/** Calculate the longest data set to get the correct header length */
const getTidesHeaderLength = (data: ITideArr[], highLowHeaders: string[]) => {
  const longestHeaders = data.reduce((acc, item) => {
    const isFirstDataSetSameAsFirstHeaderSet = item.tideData[0].type.toUpperCase() === highLowHeaders[0].toUpperCase();
    /** If first data set is not same as header then the first item will be blank it will be one more column on the table */
    const vals = isFirstDataSetSameAsFirstHeaderSet ? item.tideData.length : item.tideData.length + 1;
    if (vals > acc) {
      return vals;
    }
    return acc;
  }, 0);
  return longestHeaders;
};

const extendTideHeaderColumns = (n: number, headers: string[]) => {
  const [_, ...rest] = headers;
  const headersArray = createArrayFromNumber(n).map((_, index) => {
    const newIndex = index % 2; // returns 0 or 1
    return rest[newIndex];
  });
  return [_, ...headersArray];
};

export const TidesTable = ({ data, headers }: Pick<ITidesTimetable, 'data' | 'headers'>) => {
  const isLarge = useContext(ColumnLayoutContext) !== '1-column';
  const highLowHeaders = headers.filter((val) => val === TidePeak.High || val === TidePeak.Low);

  if (isLarge) {
    const tidesColumnCount = getTidesHeaderLength(data, highLowHeaders);
    /** We add another column which will be the date field */
    const tidesColumnCountWithDateHeader = tidesColumnCount + 1;
    const tideHeaderColumns = extendTideHeaderColumns(tidesColumnCount, headers);
    return (
      <table className="AccordionTable AccordionTable--noBorders">
        <thead className="AccordionTable-head">
          <Row>
            {createArrayFromNumber(tidesColumnCountWithDateHeader).map((_, index) => {
              const val = tideHeaderColumns[index];
              return (
                <th key={index} className={classNames('h4', { 'u-textGrey': val === TidePeak.Low })}>
                  {val}
                </th>
              );
            })}
          </Row>
        </thead>
        <tbody className="AccordionTable-body AccordionTable-body--tides">
          {data.map((row, index) => {
            // If the first piece of data doesn't match the header (index 1 since 0 is name) then it stars in the next column.
            const isTidesOffset = row.tideData[0].type.toUpperCase() !== highLowHeaders[0].toUpperCase();

            let rows: Array<JSX.Element> = isTidesOffset ? [<EmptyTideCell key={0} />] : [];
            rows = rows.concat(
              row.tideData.map(({ height, type, time }) => {
                /*
                 * This keeps the times vertically aligned if the height
                 * is not provided for a certain cell.
                 * (Note: left the empty small for this case, otherwise they would never align)
                 */
                const hasHeight = height || height === 0;
                const timeClassName = classNames('u-block u-textBold');

                return (
                  <td key={time} className={classNames({ 'u-textGrey': type === TidePeak.Low })}>
                    <small className={timeClassName}>{time}</small>
                    {hasHeight && <small className="u-block"> {height}m </small>}
                  </td>
                );
              }),
            );

            if (rows.length < tidesColumnCount) {
              for (let i = rows.length; i < tidesColumnCount; i++) {
                rows.push(<EmptyTideCell key={i} />);
              }
            }

            const { day, date } = createTideHeaderDate(row.day);

            return (
              <Row key={index}>
                <th>
                  <small className="u-block">{day}</small>
                  <small className="u-block u-textRegular">{date}</small>
                </th>
                {rows}
              </Row>
            );
          })}
        </tbody>
      </table>
    );
  }
  const items = data.map((row, index) => {
    const { day, date } = createTideHeaderDate(row.day);
    return {
      key: index.toString(),
      header: (
        <span className="small" key={index}>
          <strong>{day}</strong> {date}
        </span>
      ),
      body: (
        <SmallTable key={index} classes="AccordionTable--noBorders">
          <TidesRows tideData={row.tideData} filteredHeaders={highLowHeaders} tidesColumnCount={row.tideData.length} />
        </SmallTable>
      ),
    };
  });

  return (
    <AccordionStyleFour
      className="Accordion--header-sm Accordion--content-no-margin"
      headerContent={(item) => item.header}
      bodyContent={(item) => item.body}
      defaultToOpen={false}
      openIndexAsDefault={false}
      items={items}
      restrictToOnlyOneChildOpen={false}
    />
  );
};
